import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera as CamIcon, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { api, ApiError, type Camera, type CameraWriteResult } from "../../api";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import { Select } from "../../components/Select";
import { searchItems } from "../../fuzzy";
import { useRole } from "../../hooks/useRole";
import { EmptyState, humanizeApplyErrors, Note, SettingsSection } from "./shared";

type Kind = "ai" | "view_only";

const KIND_OPTIONS: { value: Kind; label: string }[] = [
  { value: "ai", label: "Search and alerts" },
  { value: "view_only", label: "Live view only" },
];

/** "1-4, 7" -> [1, 2, 3, 4, 7] */
function parseChannels(text: string): number[] {
  const out = new Set<number>();
  for (const part of text.split(/[,\s]+/)) {
    const m = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!m) continue;
    const a = Number(m[1]);
    const b = m[2] ? Number(m[2]) : a;
    for (let n = Math.min(a, b); n <= Math.max(a, b) && n <= 256; n++) if (n >= 1) out.add(n);
  }
  return [...out].sort((x, y) => x - y);
}

type AddDraft = { nvr_id: string; channels: string; role: Kind };
type EditDraft = { cam: Camera; name: string; role: Kind; enabled: boolean };

/** A customer's cameras, each one channel of one of their video recorders. */
export function CustomerCamerasSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { role } = useRole();
  const { scope, scopeParams, scopeKey, scopeLabel, stores } = useScope();
  const cams = useQuery({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
  });
  const allCams = useQuery({ queryKey: ["cameras", "all"], queryFn: () => api.cameras.list() });
  const nvrs = useQuery({ queryKey: ["nvrs"], queryFn: api.nvrs.list });
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState<AddDraft | null>(null);
  const [editing, setEditing] = useState<EditDraft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Camera | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const wrote = (results: CameraWriteResult[]) => {
    setError(null);
    const errs = results.flatMap((r) => r.apply_errors);
    setWarning(errs.length ? humanizeApplyErrors(errs, role) : null);
    qc.invalidateQueries({ queryKey: ["cameras"] });
    qc.invalidateQueries({ queryKey: ["camera-capacity"] });
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["profiles"] });
    qc.invalidateQueries({ queryKey: ["monitoring"] });
  };
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const nvrById = useMemo(
    () => new Map((nvrs.data ?? []).map((n) => [n.id, n])),
    [nvrs.data],
  );
  const recorderName = (id?: string | null) => {
    const n = id ? nvrById.get(id) : undefined;
    return n ? n.name || n.brand : "";
  };

  const add = useMutation({
    mutationFn: async (d: AddDraft) => {
      const nvr = nvrById.get(d.nvr_id);
      if (!nvr) throw new Error("Choose a video recorder.");
      const taken = new Set(
        (allCams.data ?? []).filter((c) => c.nvr_id === nvr.id).map((c) => c.channel),
      );
      const channels = parseChannels(d.channels).filter((ch) => !taken.has(ch));
      const results: CameraWriteResult[] = [];
      try {
        for (const ch of channels) {
          results.push(
            await api.cameras.upsert({
              id: `${nvr.id}-ch${ch}`,
              name: `${nvr.name || "Camera"} ${ch}`,
              rtsp_url: "",
              sub_rtsp_url: "",
              nvr_id: nvr.id,
              channel: ch,
              fps: 5,
              sample_interval_s: 5,
              enabled: true,
              store: nvr.store ?? "",
              role: d.role,
              max_height: 0,
              people_description: null,
              activity_description: null,
            }),
          );
        }
      } finally {
        if (results.length) wrote(results);
      }
      return results;
    },
    onSuccess: () => setAdding(null),
    onError: fail("Could not add the cameras."),
  });
  const save = useMutation({
    mutationFn: (d: EditDraft) =>
      api.cameras.update({ ...d.cam, name: d.name.trim(), role: d.role, enabled: d.enabled }),
    onSuccess: (r) => {
      setEditing(null);
      wrote([r]);
    },
    onError: fail("Could not save the camera."),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.cameras.delete(id),
    onSuccess: (r) => {
      setConfirmDelete(null);
      wrote([r]);
    },
    onError: (e: unknown) => {
      setConfirmDelete(null);
      fail("Could not remove the camera.")(e);
    },
  });

  const storeLabel = (name?: string) =>
    stores.find((s) => s.name === name)?.display_name || name || "";
  const list = cams.data ?? [];
  const visible = useMemo(
    () =>
      searchItems(list, query, (c) => [c.name, storeLabel(c.store), recorderName(c.nvr_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, query, stores, nvrById],
  );
  const recorders = (nvrs.data ?? []).filter(
    (n) => scope.kind !== "store" || n.store === scope.store,
  );
  const openAdd = () =>
    setAdding({ nvr_id: recorders[0]?.id ?? "", channels: "1-4", role: "ai" });

  return (
    <SettingsSection
      title="Cameras"
      description={
        scope.kind === "all" ? "Cameras at all your locations." : `Cameras in “${scopeLabel}”.`
      }
      hint={
        <>
          Each camera is one channel of a video recorder. Cameras set to{" "}
          <b>Search and alerts</b> can be searched and raise alerts; the others
          can still be watched live and played back.
        </>
      }
      actions={
        canEdit && (
          <button
            className="bf-button disabled:opacity-40"
            onClick={openAdd}
            disabled={recorders.length === 0}
            title={recorders.length === 0 ? "Add a video recorder first" : undefined}
          >
            <Plus size={16} className="mr-2" /> Add cameras
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}
      {warning && <Note tone="warning">{warning}</Note>}

      {list.length > 6 && (
        <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2">
          <Search size={15} className="shrink-0 text-bf-muted" />
          <input
            className="w-full bg-transparent text-sm text-bf-text outline-none placeholder:text-bf-muted"
            placeholder="Search cameras by name, location or recorder…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search cameras"
          />
        </label>
      )}

      {cams.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading cameras…</div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={CamIcon}
          title="No cameras yet"
          hint={
            recorders.length === 0
              ? "Add your video recorder first, then pick which of its channels to show here."
              : "Pick which channels of your video recorder to show here."
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState title="No camera matches that search." />
      ) : (
        <div className="bf-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Location</th>
                <th className="p-3">Video recorder</th>
                <th className="p-3">Use</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="p-3">
                    <div className="font-medium">{c.name || `Channel ${c.channel}`}</div>
                    {!c.enabled && <div className="text-xs text-bf-muted">Switched off</div>}
                  </td>
                  <td className="p-3">{storeLabel(c.store)}</td>
                  <td className="p-3 text-bf-muted">
                    {c.nvr_id ? `${recorderName(c.nvr_id)} · channel ${c.channel}` : "—"}
                  </td>
                  <td className="p-3 text-bf-muted">
                    {(c.role ?? "ai") === "ai" ? "Search and alerts" : "Live view only"}
                  </td>
                  <td className="p-3 text-right">
                    {canEdit && !c.parent_id && (
                      <div className="flex justify-end gap-3">
                        <button
                          className="text-bf-muted hover:text-bf-text"
                          aria-label={`Edit ${c.name || c.id}`}
                          onClick={() =>
                            setEditing({
                              cam: c,
                              name: c.name,
                              role: c.role ?? "ai",
                              enabled: c.enabled,
                            })
                          }
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="text-bf-muted hover:text-red-400"
                          aria-label={`Remove ${c.name || c.id}`}
                          onClick={() => setConfirmDelete(c)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <Modal onClose={() => setAdding(null)} labelledBy="cameras-add-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(adding);
            }}
          >
            <h3 id="cameras-add-title" className="text-lg font-medium text-white">
              Add cameras
            </h3>
            <div>
              <span className="mb-1 block text-xs text-bf-muted">Video recorder</span>
              <Select
                ariaLabel="Video recorder"
                value={adding.nvr_id}
                onChange={(v) => setAdding({ ...adding, nvr_id: v })}
                options={recorders.map((n) => ({
                  value: n.id,
                  label: `${n.name || n.brand} · ${storeLabel(n.store)}`,
                }))}
              />
            </div>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Channels</span>
              <input
                className="bf-input"
                value={adding.channels}
                onChange={(e) => setAdding({ ...adding, channels: e.target.value })}
                placeholder="1-4, 7"
                required
              />
              <span className="mt-1 block text-xs text-bf-muted">
                The channel numbers your cameras use on the recorder, like “1-8”
                or “1, 2, 5”. Channels already added are skipped.
              </span>
            </label>
            <div>
              <span className="mb-1 block text-xs text-bf-muted">Use</span>
              <Select
                ariaLabel="Use"
                value={adding.role}
                onChange={(v) => setAdding({ ...adding, role: v as Kind })}
                options={KIND_OPTIONS}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setAdding(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button disabled:opacity-40"
                type="submit"
                disabled={add.isPending || !adding.nvr_id || parseChannels(adding.channels).length === 0}
              >
                {add.isPending ? "Adding…" : "Add cameras"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} labelledBy="camera-edit-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(editing);
            }}
          >
            <h3 id="camera-edit-title" className="text-lg font-medium text-white">
              Edit camera
            </h3>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Name</span>
              <input
                className="bf-input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Front door"
                autoFocus
              />
            </label>
            <div>
              <span className="mb-1 block text-xs text-bf-muted">Use</span>
              <Select
                ariaLabel="Use"
                value={editing.role}
                onChange={(v) => setEditing({ ...editing, role: v as Kind })}
                options={KIND_OPTIONS}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-bf-text">
              <input
                type="checkbox"
                className="h-4 w-4 accent-bf-violet"
                checked={editing.enabled}
                onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
              />
              Camera is on
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
              <button className="bf-button disabled:opacity-40" type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Remove “{confirmDelete.name || confirmDelete.id}”?
            </h3>
            <p className="text-sm text-bf-muted">
              It stops showing in IntraScene. Your video recorder keeps
              recording it as before.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bf-button bg-white/10 bg-none"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={del.isPending}
                onClick={() => del.mutate(confirmDelete.id)}
              >
                {del.isPending ? "Removing…" : "Remove camera"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
